import React, { useState, useEffect } from 'react';
import { ChevronRight, Plus, Edit, Trash2 } from 'lucide-react';
import apiService from '../../services/api/api.service';

const News = ({ uniqueTitle }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNewsId, setEditingNewsId] = useState(null);
  const [productId, setProductId] = useState(null);
  const [formData, setFormData] = useState({
    newsHeading: '',
    newsLink: '',
    newsDescription: '',
    newsImage: '',
    newsDate: '',
    newsWebsite: ''
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchNews();
  }, [uniqueTitle]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const result = await apiService.getProductByTitle(uniqueTitle);
      
      // Debug logging to verify the API response structure
    
      
      if (result?.success && result?.data) {
        // Store productId for later use
        setProductId(result.data._id);
        
   
        
        
        if (result.data.press && Array.isArray(result.data.press)) {
          // Make sure IDs are properly handled
          const pressWithConsistentIds = result.data.press.map(item => ({
            ...item,
            id: item.id || item._id // Ensure we have a consistent id property
          }));
          
          // Make sure to sort news by date if available, with newest first
          const sortedNews = [...pressWithConsistentIds].sort((a, b) => {
            if (!a.newsDate || !b.newsDate) return 0;
            return new Date(b.newsDate) - new Date(a.newsDate);
          });
          

          setNews(sortedNews);
        } else {
          console.warn("No news data found or data is not an array:", result.data.press);
          setNews([]);
        }
      } else {
        throw new Error('Product not found');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      newsHeading: '',
      newsLink: '',
      newsDescription: '',
      newsImage: '',
      newsDate: '',
      newsWebsite: ''
    });
    setEditingNewsId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (newsItem) => {
    // Format date to YYYY-MM-DD for input[type="date"]
    let formattedDate = '';
    if (newsItem.newsDate) {
      const date = new Date(newsItem.newsDate);
      formattedDate = date.toISOString().split('T')[0];
    }

    setFormData({
      newsHeading: newsItem.newsHeading || '',
      newsLink: newsItem.newsLink || '',
      newsDescription: newsItem.newsDescription || '',
      newsImage: newsItem.newsImage || '',
      newsDate: formattedDate,
      newsWebsite: newsItem.newsWebsite || ''
    });
    
    setEditingNewsId(newsItem.id);
    setShowAddForm(true);
    window.scrollTo(0, 0); // Scroll to top to see the form
  };

  const handleDelete = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this news item?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.deleteProductNews(productId, newsId);
      
      if (response.success) {
        // Remove the deleted item from state
        setNews(prevNews => prevNews.filter(item => item.id !== newsId));
        setSuccessMessage('News deleted successfully!');
        
        // Clear success message after a delay
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to delete news');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete news');
      
      // Clear error message after a delay
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      if (!productId) {
        const productResult = await apiService.getProductByTitle(uniqueTitle);
        if (!productResult?.success || !productResult?.data?._id) {
          throw new Error('Product not found');
        }
        setProductId(productResult.data._id);
      }

      let response;
      
      // If we're editing an existing news item
      if (editingNewsId) {
        response = await apiService.updateProductNews(productId, editingNewsId, formData);
        
        // Log the update response structure
     
        
        if (response.success) {
          setSuccessMessage('News updated successfully!');
          
          // Update the item in the current state
          setNews(prevNews => 
            prevNews.map(item => 
              item.id === editingNewsId ? {...response.data, id: response.data._id || response.data.id} : item
            )
          );
        }
      } 
      // Otherwise we're adding a new news item
      else {
        response = await apiService.addProductNews(productId, formData);
        
        // Log the add response structure
  
        
        if (response.success) {
          setSuccessMessage('News added successfully!');
          
          // Add the new item to the current state
          if (response.data) {
            // Ensure we have a consistent id property
            const newItem = {
              ...response.data,
              id: response.data._id || response.data.id
            };
            
      
            setNews(prevNews => [newItem, ...prevNews]);
          } else {
            console.warn("Response success but no data returned:", response);
          }
        }
      }

      // Reset form after successful operation
      resetForm();
      
      // Close form after a delay
      setTimeout(() => {
        setShowAddForm(false);
        setSuccessMessage('');
        
        // Refresh data to ensure we have the latest
        fetchNews();
      }, 2000);
      
    } catch (err) {
      setError(err.message || 'Failed to save news');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    setShowAddForm(false);
  };

  if (loading && !news.length) return <div className="p-4 text-center text-gray-700">Loading latest news...</div>;
  if (error && !news.length) return <div className="p-4 text-red-500 text-center">Error: {error}</div>;

  return (
    <div className="bg-white rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold">Latest News</h3>
        <button 
          onClick={() => {
            resetForm();
            setShowAddForm(!showAddForm);
          }}
          className="flex items-center text-xs bg-blue-600 text-white px-2 py-1 rounded"
        >
          <Plus size={14} className="mr-1" /> Add News
        </button>
      </div>

      {/* Success or error messages outside the form */}
      {successMessage && (
        <div className="bg-green-100 text-green-700 p-2 mb-3 rounded text-xs">
          {successMessage}
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 text-red-700 p-2 mb-3 rounded text-xs">
          {error}
        </div>
      )}

      {showAddForm && (
        <div className="border rounded-lg p-3 mb-4 bg-gray-50">
          <h4 className="text-sm font-medium mb-2">
            {editingNewsId ? 'Edit News Item' : 'Add News Item'}
          </h4>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-2">
              <label className="block text-xs text-gray-600 mb-1">Headline *</label>
              <input
                type="text"
                name="newsHeading"
                value={formData.newsHeading}
                onChange={handleChange}
                className="w-full text-sm p-2 border rounded"
                required
              />
            </div>
            <div className="mb-2">
              <label className="block text-xs text-gray-600 mb-1">Link *</label>
              <input
                type="url"
                name="newsLink"
                value={formData.newsLink}
                onChange={handleChange}
                className="w-full text-sm p-2 border rounded"
                required
              />
            </div>
            <div className="mb-2">
              <label className="block text-xs text-gray-600 mb-1">Description</label>
              <textarea
                name="newsDescription"
                value={formData.newsDescription}
                onChange={handleChange}
                className="w-full text-sm p-2 border rounded"
                rows="2"
              />
            </div>
            <div className="mb-2">
              <label className="block text-xs text-gray-600 mb-1">Image URL</label>
              <input
                type="url"
                name="newsImage"
                value={formData.newsImage}
                onChange={handleChange}
                className="w-full text-sm p-2 border rounded"
              />
            </div>
            <div className="mb-2">
              <label className="block text-xs text-gray-600 mb-1">Date</label>
              <input
                type="date"
                name="newsDate"
                value={formData.newsDate}
                onChange={handleChange}
                className="w-full text-sm p-2 border rounded"
              />
            </div>
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">Source Website</label>
              <input
                type="text"
                name="newsWebsite"
                value={formData.newsWebsite}
                onChange={handleChange}
                className="w-full text-sm p-2 border rounded"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitLoading}
                className="bg-blue-600 text-white px-3 py-1 rounded text-xs"
              >
                {submitLoading ? 'Saving...' : (editingNewsId ? 'Update' : 'Save')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading && news.length > 0 && (
        <div className="text-center py-2 text-gray-500 text-sm">
          Refreshing news data...
        </div>
      )}

      {news.length > 0 ? (
        <div className="space-y-3">
          {news.map((item) => (
            <div key={item.id || item._id} className="border-b border-gray-200 pb-3 last:border-b-0">
              <div className="flex justify-between items-start">
                <div className="text-xs text-gray-500 mb-1">
                  {item.newsDate ? new Date(item.newsDate).toLocaleDateString() : 'Date unavailable'}
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleEdit(item)}
                    className="text-gray-500 hover:text-blue-600"
                    title="Edit"
                  >
                    <Edit size={14} />
                  </button>
                  <button 
                    onClick={() => handleDelete(item.id || item._id)}
                    className="text-gray-500 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <h4 className="text-sm font-medium mb-1 line-clamp-2">
                {item.newsHeading || 'News headline unavailable'}
              </h4>
              <p className="text-xs text-gray-600 line-clamp-3">
                {item.newsDescription || 'No summary available'}
              </p>
              {item.newsImage && (
                <img 
                  src={item.newsImage} 
                  alt="News" 
                  className="mt-2 rounded-lg w-full max-h-40 object-cover"
                />
              )}
              {item.newsLink && (
                <a 
                  href={item.newsLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-xs text-blue-600 font-medium flex items-center mt-2"
                >
                  Read more <ChevronRight size={12} />
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500 border rounded-lg text-sm">
          No news articles available for this company
        </div>
      )}
    </div>
  );
};

export default News;